import { useState } from 'react'
import { Redirect } from 'react-router-dom'

import { useApplyTemplateMutation } from '../../../../../generated/graphql'
import ConfirmApplyTemplateInactiveCourseModal from './ConfirmApplyTemplateInactiveCourseModal'
import useModal from '../../../../../lib/hooks/useModal'
import useNotification from '../../../../../lib/hooks/useNotification'
import { FlatPlanItem } from '../../../../../types'

type ConfirmApplyTemplateInactiveCourseModalWrapperProps = {
  /** ID of Template */
  id: string
  /** The list of plan items that will not be active for the terms they will be assigned to */
  inactiveCourses: Array<FlatPlanItem>
  /** Determines visibility of modal */
  isOpen: boolean
  /** Current template page settings */
  search: string
  /* The term to start with when applying the template */
  startTermCode: string
  /* The list of student external Ids to apply the template to */
  studentExternalIds: Array<string>
  /* The message that the advisor/admin wants displayed after the template is applied */
  templateMessage: string
}

const ConfirmApplyTemplateInactiveCourseModalWrapper = ({
  id,
  inactiveCourses,
  isOpen,
  search,
  startTermCode,
  studentExternalIds,
  templateMessage
}: ConfirmApplyTemplateInactiveCourseModalWrapperProps) => {
  const { hideModal } = useModal()
  const { showErrorNotif } = useNotification()
  const templatePath = `/templates/plan/${id}`
  const errorMsg = 'Template could not be applied.'
  const [applyTemplateCompleted, setApplyTemplateCompleted] = useState(false)
  const [applyTemplateMutation, mutation] = useApplyTemplateMutation()

  const onConfirm = () => {
    applyTemplateMutation({
      variables: {
        input: {
          id,
          studentIds: studentExternalIds,
          startTerm: startTermCode,
          templateMessage,
          throwErrorIfInactiveCourses: false
        }
      },
      onCompleted: (data): void => {
        if (data?.applyTemplate?.correlationId) {
          setApplyTemplateCompleted(true)
        }
        hideModal()
      },
      onError: () => {
        mutation.reset()
        showErrorNotif(errorMsg, onConfirm, 'Retry')
        hideModal()
      }
    })
  }

  if (applyTemplateCompleted) {
    return (
      <Redirect
        to={{
          pathname: `${templatePath}${search}`,
          state: {
            success: true,
            search,
            correlationId: mutation.data?.applyTemplate.correlationId,
            applyTemplateStudentCount: mutation.data?.applyTemplate.studentCount
          }
        }}
      />
    )
  }

  if (!mutation.called) {
    return (
      <ConfirmApplyTemplateInactiveCourseModal
        inactiveCourses={inactiveCourses}
        isOpen={isOpen}
        onConfirm={onConfirm}
      />
    )
  }

  return null
}

export default ConfirmApplyTemplateInactiveCourseModalWrapper
